import React, { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Table, InputNumber, Input, Button, Typography, Divider, Row, Col, Card, Checkbox, Tooltip, Alert, Tag, Modal, Upload } from 'antd';
import { PlusOutlined, DownOutlined, RightOutlined, FilePdfOutlined, EditOutlined, CheckCircleOutlined, UploadOutlined, EyeOutlined } from '@ant-design/icons';
import SignatureCanvas from 'react-signature-canvas';
import useOrderStore from '../../store/orderStore';
import { NewOrder } from '../../models/orders/newOrder';
import { Order } from '../../models/orders/order';
import { notifyError } from '../../components/NotificationProvider';
import NewOrderForm from './NewOrderForm';
import { FormikValues } from 'formik';
import DataTable, { DataTableColumn } from '../../components/DataTable/DataTable';
import { OrderStatus } from '../../models/orders/orderStatus';
import useStandStore from '../../store/standStore';
import { UserRole } from '../../models/users';
import { useProductStore } from '../../store/productStore';
import useOrderItemStore from '../../store/orderItemStore';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../store/authUserStore';
import _ from 'lodash';
import ModalUtil from '../../store/core/modalUtil';
import { v4 as uuidv4 } from 'uuid';
import CalculationInput from '../../components/CalculationInput';
import useStandTypeStore from '../../store/standTypeStore';
import StandTypeSelectModal from '../../components/StandTypeSelectModal';
import agent from '../../api/agent';

interface StandDetailsProps {
  standId?: string;
}

const StandDetails: React.FC<StandDetailsProps> = ({ standId }) => {
  const { Title, Paragraph } = Typography;
  const { items: standTypes, fetchItems: fetchStandTypes } = useStandTypeStore();

  useEffect(() => { fetchStandTypes(); }, [fetchStandTypes]);

  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [downloadingInvoice, setDownloadingInvoice] = useState(false);

  const { id: paramId } = useParams<{ id: string }>();
  const id = standId || paramId;
  const { selectedItem: selectedStand, fetchItemById: fetchStandById, updateItem: updateStand, setSelectedItem } = useStandStore();
  const { productsOfSelectedEvent, fetchProductsByEvent } = useProductStore();
  const { selectedStandOrders, createItem: createOrder, deleteItem: deleteOrder, downloadPDFByBillType, fetchOrdersByStandId, updateItem: updateOrder } = useOrderStore();
  const { currentUser } = useAuthStore();
  const { items: orderItemsByEvent, createItem: createOrderItem, deleteItem: deleteOrderItem, updateItem: updateOrderItem, fetchOrderItemsByEventId, setSelectedItem: setSelectedOrderItem } = useOrderItemStore();
  const [newOrder, setNewOrder] = useState<NewOrder>(() => ({
    id: uuidv4(),
    standId: id || '',
    orderNumber: uuidv4().substring(0, 8).toUpperCase(),
    totalPrice: 0,
    customerId: selectedStand?.customerId || '',
    status: OrderStatus.Pending,
    eventId: selectedStand?.eventId || ''
  }));
  const { t } = useTranslation();
  const [editingOrderItems, setEditingOrderItems] = useState<{ [key: string]: any }>({});
  const [saving, setSaving] = useState<{ [key: string]: boolean }>({});
  const [collapsedSections, setCollapsedSections] = useState<{ [orderId: string]: { [section in SectionKey]?: boolean } }>({});

  useEffect(() => {
    if (!id) return;

    setSelectedItem(null);
    useOrderStore.setState({ selectedStandOrders: [] });

    fetchStandById(id);
    fetchOrdersByStandId(id);
  }, [id, fetchOrdersByStandId, fetchStandById, setSelectedItem]);

  useEffect(() => {
    if (selectedStand) {
      setNewOrder((prevState) => ({
        ...prevState,
        customerId: selectedStand?.customerId || '',
      }));
      fetchProductsByEvent(selectedStand?.eventId!, true);
      fetchOrderItemsByEventId(selectedStand?.eventId!, true);
    }
  }, [selectedStand, fetchProductsByEvent, fetchOrderItemsByEventId]);

  const handleCreateOrder = async (billType: string, values: FormikValues, { setSubmitting, resetForm }: any) => {
    if (!values.orderNumber) {
      notifyError(t('stand.messageNotifier.numberRequired'));
      return;
    }

    try {
      const orderToCreate: NewOrder = {
        ...newOrder,
        orderNumber: values.orderNumber,
        totalPrice: +values.totalPrice,
        customerId: selectedStand?.customerId || '',
        status: OrderStatus.Pending,
        eventId: selectedStand?.eventId || '',
        billType,
      };

      await createOrder(orderToCreate as any);

      setNewOrder({
        id: uuidv4(),
        standId: id || '',
        orderNumber: uuidv4().substring(0, 8).toUpperCase(),
        totalPrice: 0,
        customerId: selectedStand?.customerId || '',
        status: OrderStatus.Pending,
        eventId: selectedStand?.eventId || '',
      });
      resetForm();
      fetchOrdersByStandId(id!);
    } catch (error) {
      notifyError(t('stand.messageNotifier.failedToCreateOrder'));
    } finally {
      setSubmitting(false);
      ModalUtil.closeModal();
    }
  };

  const handleDeleteOrder = async (orderId: string) => {
    const order = selectedStandOrders.find(o => o.id === orderId);
    if (!order || !canModifyOrder(order)) {
      notifyError(order ? getOrderEditDisabledReason(order) : t('general.forbidden'));
      return;
    }

    try {
      await deleteOrder(orderId);
      await fetchOrdersByStandId(id!);
    } catch (error) {
      notifyError(t('stand.messageNotifier.failedToDelteOrder'));
    }
  };

  const handleDownloadPDFByBillType = async (orderId: string, key: string) => {
    try {
      await downloadPDFByBillType(orderId, key);
    } catch (error) {
      notifyError(`${t('general.errorDownloadingPDF')}: ${error}`);
    }
  };

  const [savingSignature, setSavingSignature] = useState(false);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [signaturePadActive, setSignaturePadActive] = useState(false);
  const [selectedOrderForSignature, setSelectedOrderForSignature] = useState<Order | null>(null);
  const [canvasWidth, setCanvasWidth] = useState(460);
  const [previewImageSrc, setPreviewImageSrc] = useState<string | null>(null);
  const [showImagePreview, setShowImagePreview] = useState(false);
  const signatureRef = useRef<SignatureCanvas>(null);
  const signatureContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showSignatureModal || !signaturePadActive) return;
    const timer = setTimeout(() => {
      if (signatureContainerRef.current) {
        // clientWidth includes padding; subtract the 8+8px horizontal padding
        const w = signatureContainerRef.current.clientWidth - 16;
        setCanvasWidth(w > 100 ? w : 460);
      }
    }, 150);
    return () => clearTimeout(timer);
  }, [showSignatureModal, signaturePadActive]);

  const isOrderSignatureEditable = (order: Order) => !order.signature || currentUser?.role === UserRole.ADMIN;
  const isOrderLocked = (order: Order) => (!!order.signature || !!order.isOrderConfirmedWithoutSignature) && currentUser?.role !== UserRole.ADMIN;
  const isOrderCreator = (order: Order) => currentUser?.id && order.userId === currentUser.id;
  const canEditOrder = (order: Order) => currentUser?.role === UserRole.ADMIN || isOrderCreator(order);
  const canModifyOrder = (order: Order) => canEditOrder(order) && !isOrderLocked(order);
  const canSignOrder = (order: Order) => !!order.signature || canModifyOrder(order);
  const getOrderEditDisabledReason = (order: Order) => {
    if (isOrderLocked(order)) {
      return t('stand.editOrderDisabledReasonSigned');
    }
    if (!canEditOrder(order)) {
      return t('stand.editOrderDisabledReasonCreatorOrAdmin');
    }
    return '';
  };
  const getOrderCreatorName = (order: Order) => {
    const user = order.user;
    if (!user) return order.userId || '-';
    const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
    return name || user.username || user.email || user.id;
  };

  const openSignModal = (order: Order) => {
    const allowEdit = isOrderSignatureEditable(order);
    setSelectedOrderForSignature(order);
    setSignaturePadActive(allowEdit);
    setShowSignatureModal(true);
    if (order.signature) {
      setTimeout(() => signatureRef.current?.fromDataURL(order.signature!), 300);
    }
  };

  const handleSaveSignature = async () => {
    const signaturePad = signatureRef.current;
    const signature: any = signaturePad && !signaturePad.isEmpty() ? signaturePad.toDataURL() : null;
    if (!selectedOrderForSignature) return;
    setSavingSignature(true);
    try {
      await updateOrder(selectedOrderForSignature.id, { signature } as any);
      await fetchOrdersByStandId(id!);
      setShowSignatureModal(false);
      setSignaturePadActive(false);
      setSelectedOrderForSignature(null);
    } catch (error) {
      notifyError(t('general.updateFailure'));
    } finally {
      setSavingSignature(false);
    }
  };

  const handleOrderConfirmedChange = async (order: Order, checked: boolean) => {
    if (!canModifyOrder(order)) {
      notifyError(t('general.forbidden'));
      return;
    }

    try {
      await updateOrder(order.id, { isOrderConfirmedWithoutSignature: checked } as any);
      await fetchOrdersByStandId(id!);
    } catch (error) {
      notifyError(t('general.updateFailure'));
    }
  };

  const handleOrderImageUpload = async (order: Order, file: File) => {
    if (!file || !canModifyOrder(order)) {
      if (!canModifyOrder(order)) {
        notifyError(getOrderEditDisabledReason(order));
      }
      return false;
    }

    const reader = new FileReader();
    reader.onload = async (event) => {
      const imageDataUrl = event.target?.result as string;
      try {
        await updateOrder(order.id, { image: imageDataUrl } as any);
        await fetchOrdersByStandId(id!);
      } catch (error) {
        notifyError(t('general.updateFailure'));
      }
    };
    reader.readAsDataURL(file);
    return false;
  };

  const handlePreviewImage = (imageSrc: string) => {
    setPreviewImageSrc(imageSrc);
    setShowImagePreview(true);
  };

  const handleDownloadInvoice = async (standType: string) => {
    try {
      setDownloadingInvoice(true);
      const eventId = selectedStand?.eventId;
      const customerId = selectedStand?.customerId;
      if (!eventId || !customerId) return;
      const response = await agent.Events.downloadInvoiceByCustomerId(eventId, customerId, standType);
      const blob = new Blob([response], { type: 'application/pdf' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `Quittung-${selectedStand?.customer?.companyName || 'invoice'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
      setShowInvoiceModal(false);
    } catch (error) {
      notifyError(`${t('general.errorDownloadingPDF')}: ${error}`);
    } finally {
      setDownloadingInvoice(false);
    }
  };

  const getHighlightStyle = (value: any): React.CSSProperties => {
    const hasValue = value !== null && value !== undefined && value !== '' && Number(value) !== 0;
    return hasValue
      ? { borderColor: '#faad14', boxShadow: '0 0 0 2px rgba(250, 173, 20, 0.2)' }
      : {};
  };

  const toggleSectionCollapse = (orderId: string, section: SectionKey) => {
    setCollapsedSections(prev => ({
      ...prev,
      [orderId]: {
        ...prev[orderId],
        [section]: !prev[orderId]?.[section],
      }
    }));
  };

  if (!selectedStand) {
    return <div>{t('stand.standNotFound')}</div>;
  }

  const previewModal = (
    <Modal
      title={t('order.imagePreview')}
      open={showImagePreview}
      onCancel={() => setShowImagePreview(false)}
      footer={null}
      width={720}
    >
      {previewImageSrc ? (
        <img src={previewImageSrc} alt={t('order.imagePreview')} style={{ width: '100%', maxHeight: '70vh', objectFit: 'contain' }} />
      ) : null}
    </Modal>
  );

  const deliveryNoteOrders = selectedStandOrders.filter(order => order.billType === 'deliverynote');
  const redemptionSlipOrders = selectedStandOrders.filter(order => order.billType === 'redemptionslip');

  const deliveryNoteColumns: DataTableColumn<Order>[] = [
    { header: t('order.number'), accessor: 'orderNumber' },
    {
      header: t('user.accountant'),
      accessor: 'user',
      renderCell: (order: Order) => (
        <span>{getOrderCreatorName(order)}</span>
      ),
    },
    { header: t('order.totalIssued'), accessor: 'totalIssued', type: 'number', highlight: true },
    {
      header: t('order.image'),
      accessor: 'image',
      renderCell: (order: Order) => (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
          <Upload
            beforeUpload={(file) => handleOrderImageUpload(order, file)}
            showUploadList={false}
            accept="image/*"
            disabled={!canModifyOrder(order)}
          >
            <Tooltip title={order.image ? t('order.changeImage') : t('order.uploadImage')}>
              <Button icon={<UploadOutlined />} size="small" shape="circle" />
            </Tooltip>
          </Upload>
          {order.image && (
            <Tooltip title={t('order.viewImage')}>
              <Button icon={<EyeOutlined />} size="small" shape="circle" onClick={() => handlePreviewImage(order.image!)} />
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      header: t('order.validWithoutSignature'),
      accessor: 'isOrderConfirmedWithoutSignature',
      renderCell: (order: Order) => (
        <Checkbox
          checked={!!order.isOrderConfirmedWithoutSignature}
          disabled={!canModifyOrder(order)}
          onChange={e => handleOrderConfirmedChange(order, e.target.checked)}
        />
      ),
    },
    {
      header: t('checkList.signature'),
      accessor: 'signature',
      renderCell: (order: Order) => (
        order.signature ? (
          <Button type="link" onClick={() => openSignModal(order)}>
            <Tag color="success" style={{ marginRight: 6 }}>{t('checkList.signed')}</Tag>
          </Button>
        ) : (
          <Tooltip title={canSignOrder(order) ? t('checkList.sign') : getOrderEditDisabledReason(order)}>
            <span>
              <Button
                type="link"
                onClick={() => openSignModal(order)}
                disabled={!canSignOrder(order)}
              >
                {t('checkList.sign')}
              </Button>
            </span>
          </Tooltip>
        )
      ),
    },
  ];

  const redemptionSlipColumns: DataTableColumn<Order>[] = [
    { header: t('order.number'), accessor: 'orderNumber' },
    {
      header: t('user.accountant'),
      accessor: 'user',
      renderCell: (order: Order) => (
        <span>{getOrderCreatorName(order)}</span>
      ),
    },
    { header: t('order.totalUsed'), accessor: 'totalUsed', type: 'number', highlight: true },
    { header: t('order.totalUnused'), accessor: 'totalUnused', type: 'number', highlight: true },
    { header: t('order.totalClean'), accessor: 'totalClean', type: 'number', highlight: true },
    {
      header: t('order.image'),
      accessor: 'image',
      renderCell: (order: Order) => (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
          <Upload
            beforeUpload={(file) => handleOrderImageUpload(order, file)}
            showUploadList={false}
            accept="image/*"
            disabled={!canModifyOrder(order)}
          >
            <Tooltip title={order.image ? t('order.changeImage') : t('order.uploadImage')}>
              <Button icon={<UploadOutlined />} size="small" shape="circle" />
            </Tooltip>
          </Upload>
          {order.image && (
            <Tooltip title={t('order.viewImage')}>
              <Button icon={<EyeOutlined />} size="small" shape="circle" onClick={() => handlePreviewImage(order.image!)} />
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      header: t('order.validWithoutSignature'),
      accessor: 'isOrderConfirmedWithoutSignature',
      renderCell: (order: Order) => (
        <Checkbox
          checked={!!order.isOrderConfirmedWithoutSignature}
          disabled={!canModifyOrder(order)}
          onChange={e => handleOrderConfirmedChange(order, e.target.checked)}
        />
      ),
    },
    {
      header: t('checkList.signature'),
      accessor: 'signature',
      renderCell: (order: Order) => (
        order.signature ? (
          <Button type="link" onClick={() => openSignModal(order)}>
            <Tag color="success" style={{ marginRight: 6 }}>{t('checkList.signed')}</Tag>
          </Button>
        ) : (
          <Tooltip title={canSignOrder(order) ? t('checkList.sign') : getOrderEditDisabledReason(order)}>
            <span>
              <Button
                type="link"
                onClick={() => openSignModal(order)}
                disabled={!canSignOrder(order)}
              >
                {t('checkList.sign')}
              </Button>
            </span>
          </Tooltip>
        )
      ),
    },
  ];

  const deliveryNoteDownloadColumns: { key: string, displayValue: string }[] = [
    { key: 'deliverynote', displayValue: t('stand.deliveryNote') },
  ];

  const redemptionSlipDownloadColumns: { key: string, displayValue: string }[] = [
    { key: 'redemptionslip', displayValue: t('stand.redemptionSlip') },
  ];

  const getOrderItemData = (order: Order, productId: string) => {
    const orderItem = orderItemsByEvent.find(
      oi => oi.orderId === order.id && oi.productId === productId
    );
    return orderItem || {
      id: undefined,
      orderId: order.id,
      productId,
      issuedItems: { multiplier: 0, unitsPerBox: 0, sticksMultiplier: 0, unitsPerStick: getUnitsPerStick(productId), singleUnits: 0, totalQuantity: 0, remark: '' },
      usedItems: { multiplier: 0, unitsPerBox: 0, sticksMultiplier: 0, unitsPerStick: getUnitsPerStick(productId), singleUnits: 0, totalQuantity: 0, remark: '' },
      unusedItems: { multiplier: 0, unitsPerBox: 0, sticksMultiplier: 0, unitsPerStick: getUnitsPerStick(productId), singleUnits: 0, totalQuantity: 0, remark: '' },
      cleanItems: { multiplier: 0, unitsPerBox: 0, sticksMultiplier: 0, unitsPerStick: getUnitsPerStick(productId), singleUnits: 0, totalQuantity: 0, remark: '' },
    };
  };

  const getUnitsPerBox = (productId: string): number => {
    const product = (productsOfSelectedEvent || []).find((p: any) => p.id === productId);
    return product?.quantityPerBox ?? 0;
  };

  const getUnitsPerStick = (productId: string): number => {
    const product = (productsOfSelectedEvent || []).find((p: any) => p.id === productId);
    return product?.quantityPerStick ?? 0;
  };

  const getEditKey = (orderId: string, productId: string, section: string) => `${orderId}_${productId}_${section}`;

  const safeNumber = (value: any, fallback = 0): number => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  type SectionKey = 'issuedItems' | 'usedItems' | 'unusedItems' | 'cleanItems';
  const handleInlineChange = (orderId: string, productId: string, section: SectionKey, field: string, value: any) => {
    const order = selectedStandOrders.find(order => order.id === orderId);
    if (!order || !canModifyOrder(order)) {
      return;
    }
    const key = getEditKey(orderId, productId, section);
    setEditingOrderItems(prev => {
      const orderItem = orderItemsByEvent.find(
        oi => oi.productId === productId && oi.orderId === orderId
      );
      const prevSection = prev[key] || {};
      const base = {
        multiplier: 0,
        unitsPerBox: getUnitsPerBox(productId),
        sticksMultiplier: 0,
        unitsPerStick: getUnitsPerStick(productId),
        singleUnits: 0,
        totalQuantity: 0,
        remark: ''
      };
      const sectionFromOrderItem = orderItem && orderItem[section] ? orderItem[section] : {};
      let mergedSection: any = { ...base, ...sectionFromOrderItem, ...prevSection, [field]: value };

      // If multiplier is set to 0, clear the units per box to avoid leftover autofill.
      if (field === 'multiplier') {
        if (safeNumber(value, 0) === 0) {
          mergedSection.unitsPerBox = 0;
        } else if (!mergedSection.unitsPerBox) {
          mergedSection.unitsPerBox = getUnitsPerBox(productId);
        }
      }

      // If sticks multiplier cleared, clear unitsPerStick similarly.
      if (field === 'sticksMultiplier') {
        if (safeNumber(value, 0) === 0) {
          mergedSection.unitsPerStick = 0;
        } else if (!mergedSection.unitsPerStick) {
          mergedSection.unitsPerStick = getUnitsPerStick(productId);
        }
      }

      return {
        ...prev,
        [key]: mergedSection,
      };
    });
  };

  const handleSaveInlineOrderItem = async (order: Order, productId: string, section: string) => {
    if (!canModifyOrder(order)) {
      notifyError(getOrderEditDisabledReason(order));
      return;
    }
    const key = getEditKey(order.id, productId, section);
    setSaving(prev => ({ ...prev, [key]: true }));
    try {
      const edited = editingOrderItems[key] || {};
      const unitsPerBox =
        edited.unitsPerBox !== undefined
          ? safeNumber(edited.unitsPerBox, getUnitsPerBox(productId))
          : getUnitsPerBox(productId);
      const unitsPerStick =
        edited.unitsPerStick !== undefined
          ? safeNumber(edited.unitsPerStick, getUnitsPerStick(productId))
          : getUnitsPerStick(productId);

      const totalQuantity =
        safeNumber(edited.multiplier, 0) * unitsPerBox +
        safeNumber(edited.sticksMultiplier, 0) * unitsPerStick +
        safeNumber(edited.singleUnits, 0);

      const existingOrderItem = orderItemsByEvent.find(
        oi => oi.orderId === order.id && oi.productId === productId
      );

      let payload: any = {};

      const sections = ['issuedItems', 'usedItems', 'unusedItems', 'cleanItems'] as const;
      type SectionKey = typeof sections[number];
      sections.forEach((sec) => {
        if (sec === section) {
          payload[sec] = {
            ...((existingOrderItem && existingOrderItem[sec as SectionKey]) || {}),
            ...edited,
            unitsPerBox,
            unitsPerStick,
            totalQuantity,
          };
        } else if (existingOrderItem && existingOrderItem[sec as SectionKey]) {
          payload[sec] = { ...existingOrderItem[sec as SectionKey] };
        } else {
          payload[sec] = {
            multiplier: 0,
            unitsPerBox: getUnitsPerBox(productId),
            sticksMultiplier: 0,
            unitsPerStick: getUnitsPerStick(productId),
            singleUnits: 0,
            totalQuantity: 0,
          };
        }
      });

      if (existingOrderItem && existingOrderItem.id) {
        await updateOrderItem(existingOrderItem.id, payload);
      } else {
        await createOrderItem({
          ...payload,
          id: uuidv4(),
          orderId: order.id,
          productId,
          eventId: selectedStand?.eventId,
          order: { id: order.id },
          product: { id: productId },
        });
      }
      setEditingOrderItems(prev => {
        const copy = { ...prev };
        delete copy[key];
        return copy;
      });
      await fetchOrderItemsByEventId(selectedStand?.eventId!, true);
    } catch (err) {
      notifyError(t('general.updateFailure'));
    } finally {
      setSaving(prev => ({ ...prev, [key]: false }));
    }
  };

  const renderSectionTable = (order: Order, section: SectionKey, sectionTitle: string, color: string) => {
    const eventProducts = (productsOfSelectedEvent || []).slice();
    eventProducts.sort((a, b) => {
      const aIdx = typeof a.productIndex === 'number' ? a.productIndex : Infinity;
      const bIdx = typeof b.productIndex === 'number' ? b.productIndex : Infinity;
      return aIdx - bIdx;
    });

    const isCollapsed = collapsedSections[order.id]?.[section];
    const collapseIconColor = "#52c41a";
    const canModify = canModifyOrder(order);

    return (
      <div style={{ background: color, padding: 12, borderRadius: 8, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', marginBottom: 8 }}>
          <Typography.Title
            level={5}
            style={{ margin: 0, flex: 1, userSelect: 'none', display: 'flex', alignItems: 'center' }}
            onClick={() => toggleSectionCollapse(order.id, section)}
          >
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                marginRight: 8,
                fontWeight: 700,
                fontSize: 18,
                color: collapseIconColor
              }}
            >
              {isCollapsed
                ? <RightOutlined style={{ color: collapseIconColor, fontSize: 18 }} />
                : <DownOutlined style={{ color: collapseIconColor, fontSize: 18 }} />}
            </span>
            {sectionTitle}
          </Typography.Title>
          <Button
            type="link"
            size="small"
            onClick={() => toggleSectionCollapse(order.id, section)}
            style={{ marginLeft: 8, padding: 0, color: collapseIconColor, fontWeight: 600 }}
          >
            {isCollapsed ? t('general.showAllEvent') || 'Expand' : t('general.cancel') || 'Collapse'}
          </Button>
        </div>
        {!isCollapsed && (
          <Table
            dataSource={eventProducts.map(product => {
              const orderItem = getOrderItemData(order, product.id);
              const key = getEditKey(order.id, product.id, section);
              const editing = editingOrderItems[key] || (orderItem as any)[section] || {};
              return {
                key: product.id,
                product,
                ...editing,
              };
            })}
            pagination={false}
            size="small"
            rowClassName={(row: any) => {
              const unitsPerBox = row.unitsPerBox !== undefined ? Number(row.unitsPerBox) : getUnitsPerBox(row.product.id);
              const unitsPerStick = row.unitsPerStick !== undefined ? Number(row.unitsPerStick) : getUnitsPerStick(row.product.id);
              const totalQuantity = (parseInt(row.multiplier) || 0) * unitsPerBox + (parseInt(row.sticksMultiplier) || 0) * unitsPerStick + (parseInt(row.singleUnits) || 0);
              return totalQuantity > 0 ? 'stand-details-highlight-row' : '';
            }}
            columns={[
              {
                title: t('order.productName'),
                dataIndex: ['product', 'name'],
                key: 'productName',
              },
              {
                title: t('order.multiplier'),
                dataIndex: 'multiplier',
                key: `${section}_multiplier`,
                render: (_: any, row: any) => (
                  <InputNumber
                    min={0}
                    value={row.multiplier}
                    onChange={val => handleInlineChange(order.id, row.product.id, section, 'multiplier', val as number)}
                    style={getHighlightStyle(row.multiplier)}
                    disabled={!canModify}
                  />
                ),
              },
              {
                title: t('order.unitsPerBox'),
                dataIndex: 'unitsPerBox',
                key: `${section}_unitsPerBox`,
                render: (_: any, row: any) => {
                  const upbVal = row.unitsPerBox !== undefined ? row.unitsPerBox : getUnitsPerBox(row.product.id);
                  return (
                    <InputNumber
                      min={0}
                      value={upbVal}
                      onChange={val => handleInlineChange(order.id, row.product.id, section, 'unitsPerBox', val as number)}
                      style={getHighlightStyle(upbVal)}
                      disabled={!canModify}
                    />
                  );
                },
              },
              {
                title: t('order.sticksMultiplier'),
                dataIndex: 'sticksMultiplier',
                key: `${section}_sticksMultiplier`,
                render: (_: any, row: any) => (
                  <InputNumber
                    min={0}
                    value={row.sticksMultiplier}
                    onChange={val => handleInlineChange(order.id, row.product.id, section, 'sticksMultiplier', val as number)}
                    style={getHighlightStyle(row.sticksMultiplier)}
                    disabled={!canModify}
                  />
                ),
              },
              {
                title: t('order.unitsPerStick'),
                dataIndex: 'unitsPerStick',
                key: `${section}_unitsPerStick`,
                render: (_: any, row: any) => {
                  const upsVal = row.unitsPerStick !== undefined ? row.unitsPerStick : getUnitsPerStick(row.product.id);
                  return (
                    <InputNumber
                      min={0}
                      value={upsVal}
                      onChange={val => handleInlineChange(order.id, row.product.id, section, 'unitsPerStick', val as number)}
                      style={getHighlightStyle(upsVal)}
                      disabled={!canModify}
                    />
                  );
                },
              },
              {
                title: t('order.singleUnits'),
                dataIndex: 'singleUnits',
                key: `${section}_singleUnits`,
                render: (_: any, row: any) => (
                  <CalculationInput
                    value={row.singleUnits}
                    onChange={val => handleInlineChange(order.id, row.product.id, section, 'singleUnits', val)}
                    disabled={!canModify}
                    placeholder={t('order.singleUnits')}
                    style={getHighlightStyle(row.singleUnits)}
                  />
                ),
              },
              {
                title: t('order.totalQuantity'),
                dataIndex: 'totalQuantity',
                key: `${section}_totalQuantity`,
                render: (_: any, row: any) => {
                  const unitsPerBox = row.unitsPerBox !== undefined ? Number(row.unitsPerBox) : getUnitsPerBox(row.product.id);
                  const unitsPerStick = row.unitsPerStick !== undefined ? Number(row.unitsPerStick) : getUnitsPerStick(row.product.id);
                  return (parseInt(row.multiplier) || 0) * unitsPerBox + (parseInt(row.sticksMultiplier) || 0) * unitsPerStick + (parseInt(row.singleUnits) || 0);
                },
              },
              {
                title: t('general.notes'),
                dataIndex: 'remark',
                key: `${section}_remark`,
                render: (_: any, row: any) => (
                  <Input
                    value={row.remark}
                    onChange={e => handleInlineChange(order.id, row.product.id, section, 'remark', e.target.value)}
                    style={getHighlightStyle(row.remark)}
                    disabled={!canModify}
                  />
                ),
              },
              {
                title: '',
                key: 'actions',
                render: (_: any, row: any) => {
                  const key = getEditKey(order.id, row.product.id, section);
                  const hasUnsaved = !!editingOrderItems[key];
                  return hasUnsaved && canModify ? (
                    <Button
                      type="primary"
                      size="small"
                      loading={saving[key]}
                      onClick={() => handleSaveInlineOrderItem(order, row.product.id, section)}
                    >
                      {t('general.submit')}
                    </Button>
                  ) : null;
                },
              },
            ]}
            scroll={{ x: true }}
          />
        )}
      </div>
    );
  };

  const deliveryNoteExpandedRowRender = (order: Order) => (
    <div>
      {renderSectionTable(order, 'issuedItems', t('order.issuedOrderItems'), '#e6ffe6')}
    </div>
  );

  const redemptionSlipExpandedRowRender = (order: Order) => (
    <div>
      {renderSectionTable(order, 'usedItems', t('order.usedOrderItems'), '#e6e6e6')}
      <Divider />
      {renderSectionTable(order, 'unusedItems', t('order.unusedOrderItems'), '#e6f7ff')}
      <Divider />
      {renderSectionTable(order, 'cleanItems', t('order.cleanOrderItems'), '#f6ffed')}
    </div>
  );

  const onOpenOrderModal = () => {
    ModalUtil.openModal(<NewOrderForm initialValues={newOrder} onSubmit={(values, actions) => handleCreateOrder('deliverynote', values, actions)} />,
      t('stand.createDeliveryBill'), "500px");
  }

  const onOpenRedemptionSlipModal = () => {
    ModalUtil.openModal(<NewOrderForm initialValues={newOrder} onSubmit={(values, actions) => handleCreateOrder('redemptionslip', values, actions)} />,
      t('stand.createRedemptionSlip'), "500px");
  }

  return (
    <>
      <div data-tour="stand-details-header" style={{ border: '1px solid #f0f2f5', padding: '20px', borderRadius: '5px', marginBottom: '20px' }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={2} style={{ margin: 0 }}>{selectedStand.name}</Title>
            <Paragraph style={{ margin: '5px 0' }}>
              <strong>{t('stand.customer')}</strong> <Link to={`/customers/${selectedStand.customer?.id}`}>{selectedStand.customer?.companyName}</Link>
            </Paragraph>
            <Paragraph style={{ margin: '5px 0' }}>
              <strong>{t('stand.type')}</strong> {selectedStand.type?.name}
            </Paragraph>
            <Paragraph style={{ margin: '5px 0' }}>
              <strong>{t('stand.sector')}</strong> {selectedStand.sector?.name}
            </Paragraph>
          </Col>
          <Col>
            <Button type="primary" icon={<FilePdfOutlined />} onClick={() => setShowInvoiceModal(true)}>
              {t('stand.downloadInvoice')}
            </Button>
          </Col>
        </Row>
      </div>

      <StandTypeSelectModal
        open={showInvoiceModal}
        loading={downloadingInvoice}
        onConfirm={handleDownloadInvoice}
        onCancel={() => setShowInvoiceModal(false)}
      />

      <Modal
        open={showSignatureModal}
        onCancel={() => { setShowSignatureModal(false); setSignaturePadActive(false); setSelectedOrderForSignature(null); }}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <EditOutlined style={{ color: '#1677ff' }} />
            <span>{t('checkList.signature')} — {selectedOrderForSignature?.orderNumber || ''}</span>
          </div>
        }
        footer={null}
        width={560}
        destroyOnClose
      >
        {selectedOrderForSignature?.signature && !signaturePadActive ? (
          <div style={{ textAlign: 'center', padding: '8px 0' }}>
            <Tag color="success" icon={<CheckCircleOutlined />} style={{ fontSize: 13, padding: '4px 10px', borderRadius: 20, marginBottom: 16 }}>
              {t('checkList.signed')}
            </Tag>
            <img
              src={selectedOrderForSignature.signature}
              alt={t('checkList.signature')}
              style={{ display: 'block', maxWidth: '100%', maxHeight: 140, margin: '0 auto 16px', border: '1px solid #d9d9d9', borderRadius: 6 }}
            />
            {isOrderSignatureEditable(selectedOrderForSignature) && (
              <Button icon={<EditOutlined />} onClick={() => { setSignaturePadActive(true); setTimeout(() => signatureRef.current?.fromDataURL(selectedOrderForSignature.signature!), 80); }}>
                {t('checkList.sign')}
              </Button>
            )}
          </div>
        ) : (
          <div>
            <Alert
              type="info"
              showIcon
              message={t('checkList.signatureInstruction') || 'Please hand this tablet to the stand operator / customer to sign below.'}
              style={{ marginBottom: 16 }}
            />
            <div
              ref={signatureContainerRef}
              style={{
                border: '2px dashed #1677ff',
                borderRadius: 8,
                background: '#fafafa',
                padding: '8px 8px 0 8px',
                marginBottom: 4,
                display: 'block',
                width: '100%',
                boxSizing: 'border-box',
              }}
            >
              <SignatureCanvas
                ref={signatureRef}
                penColor="black"
                canvasProps={{ width: canvasWidth, height: 150, style: { display: 'block' } }}
              />
              <div
                style={{
                  borderTop: '2px solid #d9d9d9',
                  padding: '3px 0 6px',
                  fontSize: 11,
                  color: '#bbb',
                  letterSpacing: 2,
                  textAlign: 'center',
                  textTransform: 'uppercase',
                }}
              >
                {t('checkList.sign')}
              </div>
            </div>
            <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
              <Button type="primary" loading={savingSignature} onClick={handleSaveSignature}>
                {t('general.submit')}
              </Button>
              <Button onClick={() => signatureRef.current?.clear()}>
                {t('common.clear')}
              </Button>
              <Button onClick={() => { setSignaturePadActive(false); setShowSignatureModal(false); setSelectedOrderForSignature(null); }}>
                {t('general.cancel')}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      <Card data-tour="stand-delivery-bills" style={{ marginTop: '1em' }}>
        <Title level={3}>{t('stand.deliveryBills')}</Title>
        {(currentUser?.role == 'admin' || currentUser?.role == 'accountant') &&
          <Button style={{ marginBottom: '1em' }} type="primary" icon={<PlusOutlined />} onClick={onOpenOrderModal}>
            {t('stand.createDeliveryBill')}
          </Button>
        }
        <DataTable
          columns={deliveryNoteColumns}
          data={deliveryNoteOrders}
          expandedRowRender={deliveryNoteExpandedRowRender}
          onDelete={handleDeleteOrder}
          disableDelete={(order: Order) => !canModifyOrder(order)}
          deleteTooltip={(order: Order) => getOrderEditDisabledReason(order)}
          onDownload={handleDownloadPDFByBillType}
          downloadColumns={deliveryNoteDownloadColumns}
          defaultExpandAllRows
        />
      </Card>

      <Card data-tour="stand-redemption-slips" style={{ marginTop: '1em' }}>
        <Title level={3}>{t('stand.redemptionSlips')}</Title>
        {(currentUser?.role == 'admin' || currentUser?.role == 'accountant') &&
          <Button style={{ marginBottom: '1em' }} type="primary" icon={<PlusOutlined />} onClick={onOpenRedemptionSlipModal}>
            {t('stand.createRedemptionSlip')}
          </Button>
        }
        <DataTable
          columns={redemptionSlipColumns}
          data={redemptionSlipOrders}
          expandedRowRender={redemptionSlipExpandedRowRender}
          onDelete={handleDeleteOrder}
          disableDelete={(order: Order) => !canModifyOrder(order)}
          deleteTooltip={(order: Order) => getOrderEditDisabledReason(order)}
          onDownload={handleDownloadPDFByBillType}
          downloadColumns={redemptionSlipDownloadColumns}
          defaultExpandAllRows
        />
      </Card>
      {previewModal}
    </>
  );
};

export default StandDetails;


